import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import {
  getCategories,
  getProductsForCategories,
  getProductsPage,
  getProductsForCategoryTermsPage,
  searchProducts,
  searchProductsByCategoryTerm
} from '../firebase/services';
import { useCart } from '../Context/CartContext';
import ProductCard from '../Components/ProductCard';
import SEO from '../Components/SEO';

export default function Shop() {
  const { categorySlug: categorySlugParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category'); // legacy support
  const subFilter = searchParams.get('sub');
  const searchQuery = searchParams.get('search');
  const pageParamRaw = searchParams.get('page');
  const pageParam = Math.max(1, Number.parseInt(pageParamRaw || '1', 10) || 1);
  // eslint-disable-next-line no-unused-vars
  const { addToCart } = useCart();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [pagingType, setPagingType] = useState(null); // 'all' | 'category' | null
  const [categoryTerms, setCategoryTerms] = useState([]);

  const pageCursorsRef = useRef([null]);
  const lastFetchKeyRef = useRef('');

  const PAGE_SIZE = 12;

  const setPageInUrl = React.useCallback((nextPage, { replace = false } = {}) => {
    const p = Math.max(1, Number.parseInt(String(nextPage || 1), 10) || 1);
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setSearchParams(next, { replace });
  }, [searchParams, setSearchParams]);

  const toSlug = React.useCallback((value, fallback = '') => {
    const raw = (value ?? '').toString();
    const firstPart = raw.split(' / ')[0].trim().toLowerCase();
    const cleaned = firstPart
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\u0600-\u06FF-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    return cleaned || fallback;
  }, []);

  const matchesCategory = React.useCallback((cat, value) => {
    if (!cat || !value) return false;
    const v = value.toString().trim().toLowerCase();
    if (!v) return false;

    const name = (cat.name || '').toString().toLowerCase();
    const nameEn = (cat.nameEn || '').toString().toLowerCase();
    const nameAr = (cat.nameAr || '').toString().toLowerCase();

    const slugFromNameEn = toSlug(cat.nameEn || cat.name || '', '').toLowerCase();
    const slugFromName = toSlug(cat.name || '', '').toLowerCase();

    if (v === name || v === nameEn || v === nameAr) return true;
    if (v === slugFromNameEn || v === slugFromName) return true;

    // Support legacy usage where categoryFilter could be part of the customLink
    if (cat.customLink && cat.customLink.toLowerCase().includes(v)) return true;
    // Support hyphenated query
    if (name && v === name.replace(/\s+/g, '-')) return true;
    if (nameEn && v === nameEn.replace(/\s+/g, '-')) return true;
    return false;
  }, [toSlug]);

  const buildShopUrl = React.useCallback((parentCat, subCat) => {
    if (!parentCat) return '/shop';
    const parentSlug = toSlug(parentCat.nameEn || parentCat.name || parentCat.nameAr || '', '');
    const base = parentSlug ? `/shop/${parentSlug}` : '/shop';

    if (!subCat) return base;

    const subSlug = toSlug(subCat.nameEn || subCat.name || subCat.nameAr || '', '');
    if (!subSlug) return base;
    return `${base}?sub=${encodeURIComponent(subSlug)}`;
  }, [toSlug]);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const shopBaseUrl = origin ? `${origin}/shop` : '';
  const isSearchPage = Boolean(searchQuery);

  const canonicalForShop = React.useMemo(() => {
    if (!origin) return '';

    // Avoid indexing search result pages; canonicalize them to /shop
    if (searchQuery) return `${origin}/shop`;

    // Prefer the new route style: /shop/:categorySlug?sub=...
    if (categorySlugParam) {
      const u = new URL(`${origin}/shop/${categorySlugParam}`);
      if (subFilter) u.searchParams.set('sub', subFilter);
      return u.toString();
    }

    // Legacy: keep category parameter (category pages are indexable)
    if (categoryFilter) {
      const u = new URL(`${origin}/shop`);
      u.searchParams.set('category', categoryFilter);
      if (subFilter) u.searchParams.set('sub', subFilter);
      return u.toString();
    }

    return `${origin}/shop`;
  }, [origin, categorySlugParam, categoryFilter, subFilter, searchQuery]);

  useEffect(() => {
    const fetchData = async () => {
      const fetchKey = `${categorySlugParam || ''}::${categoryFilter || ''}::${subFilter || ''}::${searchQuery || ''}`;
      const filtersChanged = lastFetchKeyRef.current !== fetchKey;
      lastFetchKeyRef.current = fetchKey;

      setLoading(true);
      if (filtersChanged) {
        setProducts([]);
      }
      setNextCursor(null);
      setHasMore(false);
      setPagingType(null);
      setCategoryTerms([]);
      pageCursorsRef.current = [null];
      try {
        const allCategories = await getCategories();
        setCategories(allCategories);

        let filteredProducts = [];
        const desiredPage = pageParam;

        // Resolve parent category (new route param has priority)
        const parentCategory = categorySlugParam
          ? allCategories.find(c => !c.parentId && matchesCategory(c, categorySlugParam))
          : (categoryFilter
            ? allCategories.find(c => matchesCategory(c, categoryFilter))
            : null);

        // Resolve sub category when a parent is present
        const subCategory = (subFilter && parentCategory)
          ? allCategories
            .filter(c => c.parentId === parentCategory.id)
            .find(c => matchesCategory(c, subFilter))
          : null;

        // Active category for filtering: sub overrides parent
        const activeCategory = subCategory || parentCategory;

        // 1) Category filtering (server-side)
        if (activeCategory) {
          const selectedCategory = activeCategory;

          // Find all descendant categories (children, grandchildren, etc.)
          const getDescendants = (parentId) => {
            const children = allCategories.filter(c => c.parentId === parentId);
            let descendants = [...children];
            children.forEach(child => {
              descendants = [...descendants, ...getDescendants(child.id)];
            });
            return descendants;
          };

          const relevantCategories = selectedCategory
            ? [selectedCategory, ...getDescendants(selectedCategory.id)]
            : [];

          const relevantIds = relevantCategories.map(c => c.id).filter(Boolean);
          const relevantNames = relevantCategories.map(c => c.name).filter(Boolean);

          // Also include the raw filter string(s) to catch legacy/manual matches
          if (categoryFilter) {
            relevantNames.push(categoryFilter);
            relevantNames.push(categoryFilter.replace(/-/g, ' '));
          }
          if (categorySlugParam) {
            relevantNames.push(categorySlugParam);
            relevantNames.push(categorySlugParam.replace(/-/g, ' '));
          }
          if (subFilter) {
            relevantNames.push(subFilter);
            relevantNames.push(subFilter.replace(/-/g, ' '));
          }

          const directChildren = selectedCategory
            ? allCategories.filter(c => c.parentId === selectedCategory.id)
            : [];

          // Parent category page: fetch a small preview per subcategory (4 each) to keep reads low.
          // Leaf category page: paginate through products.
          if (selectedCategory && directChildren.length > 0) {
            const perSub = await Promise.all(
              directChildren.map(async (sc) => {
                const [byId, byName] = await Promise.all([
                  searchProductsByCategoryTerm({ term: sc.id, maxResults: 4 }),
                  searchProductsByCategoryTerm({ term: sc.name, maxResults: 4 })
                ]);
                const mergedLocal = new Map();
                [...byId, ...byName].forEach(p => {
                  if (p && p.id && !mergedLocal.has(p.id)) mergedLocal.set(p.id, p);
                });
                return Array.from(mergedLocal.values()).slice(0, 4);
              })
            );
            const merged = new Map();
            perSub.flat().forEach(p => {
              if (p && p.id && !merged.has(p.id)) merged.set(p.id, p);
            });
            filteredProducts = Array.from(merged.values());
            setHasMore(false);
            setNextCursor(null);
            setPagingType(null);
            setCategoryTerms([]);
          } else {
            const terms = Array.from(new Set([...relevantIds, ...relevantNames].map(String).filter(Boolean)));

            if (terms.length > 0 && terms.length <= 10) {
                const fetchPage = async (cursor) => getProductsForCategoryTermsPage({
                  terms,
                  pageSize: PAGE_SIZE,
                  cursor
                });

                // Prefetch cursors up to desired page
                for (let p = 1; p < desiredPage; p++) {
                  if (pageCursorsRef.current[p]) continue;
                  const prevCursor = pageCursorsRef.current[p - 1] || null;
                  const res = await fetchPage(prevCursor);
                  pageCursorsRef.current[p] = res.nextCursor;
                  if (!res.nextCursor || res.items.length === 0) break;
                }

                let effectivePage = desiredPage;
                while (effectivePage > 1 && !pageCursorsRef.current[effectivePage - 1]) {
                  effectivePage -= 1;
                }
                if (effectivePage !== desiredPage) {
                  setPageInUrl(effectivePage, { replace: true });
                }

                const wantedCursor = pageCursorsRef.current[effectivePage - 1] || null;
                const { items, nextCursor: cursor } = await fetchPage(wantedCursor);
                filteredProducts = items;
                setNextCursor(cursor);
                setHasMore(Boolean(cursor));
                setPagingType('category');
                setCategoryTerms(terms);
              } else {
              // Fallback when terms exceed Firestore's 10-term limit.
              // Still avoids reading ALL products, but may read the full category result set.
              filteredProducts = await getProductsForCategories({
                categoryIds: relevantIds,
                categoryNames: relevantNames
              });
              setHasMore(false);
              setNextCursor(null);
              setPagingType(null);
              setCategoryTerms([]);
            }
          }
        } else if (searchQuery) {
          // 2) Search-only (server-side) to avoid downloading all products
          filteredProducts = await searchProducts({ text: searchQuery, maxResults: 40 });
          setHasMore(false);
          setNextCursor(null);
          setPagingType(null);
          setCategoryTerms([]);
        } else {
          // 3) All products => paged query (lowest read cost)
          const fetchPage = async (cursor) => getProductsPage({ pageSize: PAGE_SIZE, cursor });

          for (let p = 1; p < desiredPage; p++) {
            if (pageCursorsRef.current[p]) continue;
            const prevCursor = pageCursorsRef.current[p - 1] || null;
            const res = await fetchPage(prevCursor);
            pageCursorsRef.current[p] = res.nextCursor;
            if (!res.nextCursor || res.items.length === 0) break;
          }

          let effectivePage = desiredPage;
          while (effectivePage > 1 && !pageCursorsRef.current[effectivePage - 1]) {
            effectivePage -= 1;
          }
          if (effectivePage !== desiredPage) {
            setPageInUrl(effectivePage, { replace: true });
          }

          const wantedCursor = pageCursorsRef.current[effectivePage - 1] || null;
          const { items, nextCursor: cursor } = await fetchPage(wantedCursor);
          filteredProducts = items;
          setNextCursor(cursor);
          setHasMore(Boolean(cursor));
          setPagingType('all');
          setCategoryTerms([]);
        }

        // Client-side filter when BOTH category + search are present
        if ((categoryFilter || categorySlugParam) && searchQuery) {
          const query = searchQuery.toLowerCase().trim();
          filteredProducts = filteredProducts.filter(p => {
            const nameEn = (p.nameEn || '').toLowerCase();
            const nameAr = (p.nameAr || '').toLowerCase();
            const name = (p.name || '').toLowerCase(); // Legacy combined name
            
            return nameEn.includes(query) || nameAr.includes(query) || name.includes(query);
          });
        }

        setProducts(filteredProducts);
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryFilter, categorySlugParam, subFilter, searchQuery, matchesCategory, pageParam]);

  const renderProductCard = (product) => {
    return (
      <li key={product.id}>
        <ProductCard product={product} categories={categories} />
      </li>
    );
  };

  // Determine current view context (safe even while loading)
  const selectedParentCategory = categorySlugParam
    ? categories.find(c => !c.parentId && matchesCategory(c, categorySlugParam))
    : (categoryFilter ? categories.find(c => matchesCategory(c, categoryFilter)) : null);

  const selectedSubCategory = (subFilter && selectedParentCategory)
    ? categories
      .filter(c => c.parentId === selectedParentCategory.id)
      .find(c => matchesCategory(c, subFilter))
    : null;

  const selectedCategory = selectedSubCategory || selectedParentCategory;

  const subCategories = selectedCategory
    ? categories.filter(c => c.parentId === selectedCategory.id)
    : [];

  const isFlatView = subCategories.length === 0;
  const isClientPaging = isFlatView && pagingType === null;
  const clientTotalPages = useMemo(() => {
    if (!isClientPaging) return 1;
    return Math.max(1, Math.ceil((products?.length || 0) / PAGE_SIZE));
  }, [isClientPaging, products, PAGE_SIZE]);

  const visibleProducts = useMemo(() => {
    if (!isClientPaging) return products;
    const start = (pageParam - 1) * PAGE_SIZE;
    return products.slice(start, start + PAGE_SIZE);
  }, [isClientPaging, products, pageParam, PAGE_SIZE]);

  useEffect(() => {
    if (!isClientPaging) return;
    // While server-side pagination is fetching, `pagingType` briefly resets and can
    // look like client paging. Avoid clamping the URL page during loading.
    if (loading) return;
    if (!products || products.length === 0) return;
    if (pageParam > clientTotalPages) {
      setPageInUrl(clientTotalPages, { replace: true });
    }
  }, [isClientPaging, loading, products, pageParam, clientTotalPages, setPageInUrl]);

  const shopStructuredData = React.useMemo(() => {
    if (!origin) return null;
    const crumbs = [
      { name: 'Home', item: `${origin}/` },
      { name: 'Shop', item: `${origin}/shop` }
    ];
    if (categoryFilter) {
      crumbs.push({ name: selectedCategory?.name || categoryFilter, item: canonicalForShop || `${origin}/shop` });
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: c.name,
        item: c.item
      }))
    };
  }, [origin, categoryFilter, selectedCategory, canonicalForShop]);

  const isInitialLoading = loading && products.length === 0;
  if (isInitialLoading) {
    return <div style={{minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>Loading...</div>;
  }

  const renderSidebar = () => {
    const parents = categories.filter(c => !c.parentId);
    const activeParentId = selectedParentCategory
      ? selectedParentCategory.id
      : (selectedSubCategory?.parentId || null);
    
    return (
      <aside className="shop-sidebar">
        <h3 className="sidebar-title">Categories</h3>
        <ul className="sidebar-list">
          <li>
            <Link to="/shop" className={!categoryFilter && !categorySlugParam ? "active-link" : ""}>All Products</Link>
          </li>
          {parents.map(parent => {
            const children = categories.filter(c => c.parentId === parent.id);
            const parentSlug = toSlug(parent.nameEn || parent.name || parent.nameAr || '', '');
            const isParentActive = !!activeParentId && activeParentId === parent.id;
            
            return (
              <li key={parent.id} className={`sidebar-item ${isParentActive ? "expanded" : ""}`}>
                <div className="sidebar-link-wrapper">
                    <Link 
                        to={parent.customLink || (parentSlug ? `/shop/${parentSlug}` : '/shop')} 
                        className={isParentActive && !selectedSubCategory ? "active-link" : ""}
                    >
                    {parent.name}
                    </Link>
                    {children.length > 0 && (
                        <ion-icon name={isParentActive ? "chevron-down-outline" : "chevron-forward-outline"}></ion-icon>
                    )}
                </div>
                
                {children.length > 0 && (
                  <ul className={`sidebar-sublist ${isParentActive ? "show" : ""}`}>
                    {children.map(child => {
                      const childSlug = toSlug(child.nameEn || child.name || child.nameAr || '', '');
                      const url = child.customLink || (parentSlug
                        ? `${`/shop/${parentSlug}`}?sub=${encodeURIComponent(childSlug || child.name)}`
                        : '/shop');
                      const isChildActive = !!selectedSubCategory && selectedSubCategory.id === child.id;

                      return (
                        <li key={child.id}>
                          <Link to={url} className={isChildActive ? 'active-link' : ''}>
                            {child.name}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </aside>
    );
  };

  return (
    <div className="container" style={{ padding: '40px 20px 40px' }}>
      <SEO
        title={
          categoryFilter
            ? `${selectedCategory?.name || categoryFilter} - Shop`
            : (searchQuery ? `Search: ${searchQuery}` : 'Shop')
        }
        description={
          categoryFilter
            ? `Browse ${selectedCategory?.name || categoryFilter} products at VetCare Store.`
            : (searchQuery ? `Search results for "${searchQuery}" on VetCare Store.` : 'Browse pet supplies, food, and essentials at VetCare Store.')
        }
        type="website"
        noIndex={isSearchPage}
        canonical={canonicalForShop}
        structuredData={shopStructuredData}
      />
      <div className="shop-layout">
        {/* Sidebar */}
        <div className="shop-sidebar-container desktop-only-flex">
            {renderSidebar()}
        </div>

        {/* Main Content */}
        <div className="shop-main">
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 className="h2 section-title" style={{ 
              marginBottom: '30px', 
              fontFamily: 'var(--ff-bangers)', 
              fontSize: '4rem', 
              color: 'var(--portland-orange)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              textAlign: 'center',
              width: '100%',
              display: 'block'
            }}>
              {selectedCategory ? selectedCategory.name : 'Shop'}
            </h2>
          </div>

          {products.length === 0 ? (
            <div className="text-center" style={{ padding: '40px', color: '#666' }}>
              <p>No products found in this category.</p>
            </div>
          ) : (
            <>
              {subCategories.length > 0 ? (
                // Grouped View (Parent Category)
                <div className="category-groups">
                  {subCategories.map(subCat => {
                    const subCatProducts = products.filter(p => {
                        if (p.categories && Array.isArray(p.categories)) {
                            return p.categories.includes(subCat.id) || p.categories.includes(subCat.name);
                        }
                        return p.category && p.category.toLowerCase() === subCat.name.toLowerCase();
                    });
                    
                    if (subCatProducts.length === 0) return null;

                    return (
                      <div key={subCat.id} className="subcategory-section">
                        <div className="section-header-wrapper">
                            <h3 className="h3 section-title">{subCat.name}</h3>
                            <Link 
                          to={subCat.customLink || buildShopUrl(selectedCategory, subCat)} 
                                className="view-all-btn"
                            >
                                Show All <ion-icon name="arrow-forward" aria-hidden="true"></ion-icon>
                            </Link>
                        </div>
                        <ul className="grid-list shop-grid">
                            {subCatProducts.slice(0, 4).map(product => renderProductCard(product))}
                        </ul>
                      </div>
                    );
                  })}
                  
                  {/* Products that belong directly to the parent - REMOVED as per request */}
                </div>
              ) : (
                // Flat View (Leaf Category or All Products)
                <>
                  <ul className="grid-list shop-grid">
                    {visibleProducts.map((product) => renderProductCard(product))}
                  </ul>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: '40px',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      className="btn"
                      style={{ backgroundColor: 'var(--portland-orange)', color: '#fff' }}
                      onClick={() => setPageInUrl(pageParam - 1)}
                      disabled={loading || pageParam <= 1}
                    >
                      Prev
                    </button>

                    <div style={{ color: '#6d7175', fontWeight: 600 }}>
                      {isClientPaging ? `Page ${pageParam} of ${clientTotalPages}` : `Page ${pageParam}`}
                    </div>

                    <button
                      className="btn"
                      style={{ backgroundColor: 'var(--portland-orange)', color: '#fff' }}
                      onClick={() => setPageInUrl(pageParam + 1)}
                      disabled={loading || (isClientPaging ? pageParam >= clientTotalPages : !hasMore)}
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      
      <style>{`
        .shop-layout {
          display: flex;
          gap: 40px;
        }
        .shop-sidebar-container {
            width: 250px;
            flex-shrink: 0;
        }
        .shop-main {
          flex: 1;
        }
        
        /* Sidebar Styles */
        .sidebar-title {
            font-size: 1.5rem;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid var(--portland-orange);
        }
        .sidebar-list {
            list-style: none;
            padding: 0;
        }
        .sidebar-item {
            margin-bottom: 10px;
        }
        .sidebar-link-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: pointer;
        }
        .sidebar-list a {
            color: var(--onyx);
            font-weight: 500;
            transition: color 0.3s;
            display: block;
            padding: 5px 0;
        }
        .sidebar-list a:hover, .sidebar-list a.active-link {
            color: var(--portland-orange);
        }
        .sidebar-sublist {
            padding-left: 15px;
            display: none;
            margin-top: 5px;
            border-left: 1px solid #eee;
        }
        .sidebar-sublist.show {
            display: block;
        }
        .sidebar-sublist li a {
            font-size: 0.9rem;
            color: #666;
            font-weight: 400;
        }
        
        .shop-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
        }
        
        @media (max-width: 1200px) {
          .shop-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 992px) {
            .shop-layout {
                display: block;
            }
            .shop-sidebar-container {
                display: none; /* Hide sidebar on mobile for now, or make it a drawer */
            }
          .shop-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .shop-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        @media (max-width: 575px) {
          .shop-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
        }
        
        .subcategory-section {
            margin-bottom: 60px;
            background: #fff;
            border-radius: 10px;
        }
        .section-header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 25px;
            padding-bottom: 15px;
        }
        .section-header-wrapper .section-title {
            font-size: 1.8rem;
            font-weight: 700;
            color: var(--onyx);
            margin-bottom: 0;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        .view-all-btn {
            color: var(--black);
            font-weight: 600;
            font-size: 0.9rem;
            border: 2px solid var(--black);
            padding: 6px 16px;
            border-radius: 4px;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            gap: 5px;
            text-transform: uppercase;
        }
        .view-all-btn:hover {
            background: var(--black);
            color: #fff;
        }
      `}</style>

     {/* CART FIXES - REMOVED (Handled in CartDrawer.js) */}
     <style>{`
       /* CART SIDEBAR FIXES - REMOVED */
     `}</style>
    </div>
  );
}
